import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filters } from '../types';
import { useFilterOptions } from '../hooks/useFilterOptions';
import { SunriseIcon, SunIcon, MoonIcon } from '../components/TimeSlotIcons';
import { BackButton } from '../components/BackButton';
import { Breadcrumbs } from '../components/Breadcrumbs';

interface FilterPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const FilterPage: React.FC<FilterPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { options, loading: optionsLoading, error: optionsError } = useFilterOptions();
  
  const [filters, setFilters] = useState<Filters>({
    langue: 'francais'
  });

  // État pour les disponibilités sélectionnées (jour + créneaux)
  const [selectedAvailabilities, setSelectedAvailabilities] = useState<{[jour: string]: string[]}>({});

  // Mapping des créneaux avec leurs labels et icônes
  const creneauData: Record<string, {label: string, icon: React.ComponentType<{size?: number}>}> = {
    'matin': { label: 'Matin (09h-12h)', icon: SunriseIcon },
    'apm': { label: 'Après-midi (12h-17h)', icon: SunIcon },
    'soir': { label: 'Soir (17h-21h)', icon: MoonIcon }
  };



  // Mapping des langues avec leurs labels
  const langueLabels: Record<string, string> = {
    'francais': '🇫🇷 Français',
    'anglais': '🇬🇧 Anglais'
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation des champs obligatoires
    if (!filters.langue) {
      showToast('Veuillez sélectionner une langue', 'error');
      return;
    }

    // Ajouter les disponibilités sélectionnées aux filtres
    const finalFilters = {
      ...filters,
      disponibilites: Object.keys(selectedAvailabilities).length > 0 ? selectedAvailabilities : undefined
    };
    
    
    // Navigation vers la page des tuteurs avec les filtres
    navigate('/tutors', { state: { filters: finalFilters } });
  };

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Fonctions pour gérer les disponibilités
  const toggleAvailability = (jour: string, creneau: string) => {
    setSelectedAvailabilities(prev => {
      const jourCreneaux = prev[jour] || [];
      const isSelected = jourCreneaux.includes(creneau);
      
      if (isSelected) {
        // Retirer le créneau
        const newCreneaux = jourCreneaux.filter(c => c !== creneau);
        if (newCreneaux.length === 0) {
          const { [jour]: removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [jour]: newCreneaux };
      } else {
        // Ajouter le créneau
        return { ...prev, [jour]: [...jourCreneaux, creneau] };
      }
    });
  };

  const isAvailabilitySelected = (jour: string, creneau: string) => {
    return selectedAvailabilities[jour]?.includes(creneau) || false;
  };

  // Affichage de chargement des options
  if (optionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">Chargement des options de filtrage...</p>
          <p className="text-sm text-blue-600">Récupération depuis la base de données</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Navigation */}
          <div className="mb-6">
            <BackButton to="/" label="Retour à l'accueil" />
            <Breadcrumbs />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              <i className="fas fa-search text-blue-600 mr-3"></i>
              Trouvez votre tuteur-trice idéal-e
            </h1>
            <p className="text-gray-600 text-lg">
              Précisez vos critères pour trouver le tuteur qui vous correspond
            </p>
            {optionsError && (
              <div className="mt-4 p-3 bg-yellow-100 border border-yellow-400 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Utilisation des options par défaut (erreur: {optionsError})
                </p>
              </div>
            )}
          </div>

          {/* Formulaire de filtres */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Filière */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-graduation-cap text-blue-600 mr-2"></i>
                  Filière d'études
                </label>
                <select
                  value={filters.filiere || ''}
                  onChange={(e) => updateFilter('filiere', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Toutes les filières</option>
                  {options.filieres.map(({ filiere, count }) => (
                    <option key={filiere} value={filiere}>
                      {filiere} ({count} tuteur{count > 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
              </div>

              {/* Matière */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-book text-blue-600 mr-2"></i>
                  Matière recherchée
                </label>
                <select
                  value={filters.matiere || ''}
                  onChange={(e) => updateFilter('matiere', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Toutes les matières</option>
                  {options.matieres.map(({ matiere, count }) => (
                    <option key={matiere} value={matiere}>
                      {matiere.charAt(0).toUpperCase() + matiere.slice(1)} ({count} tuteur{count > 1 ? 's' : ''})
                    </option>
                  ))}
                </select>
              </div>

              {/* Disponibilités intelligentes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-4">
                  <i className="fas fa-calendar-check text-blue-600 mr-2"></i>
                  Disponibilités souhaitées
                </label>
                <div className="space-y-4">
                  {options.jours.map(jour => {
                    const jourLabels: Record<string, string> = {
                      'lundi': 'Lundi',
                      'mardi': 'Mardi', 
                      'mercredi': 'Mercredi',
                      'jeudi': 'Jeudi',
                      'vendredi': 'Vendredi',
                      'samedi': 'Samedi',
                      'dimanche': 'Dimanche'
                    };

                    const hasSelections = selectedAvailabilities[jour]?.length > 0;

                    return (
                      <div key={jour} className={`border rounded-lg p-4 transition-all ${hasSelections ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-800">
                            📅 {jourLabels[jour]}
                          </h4>
                          {hasSelections && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                              {selectedAvailabilities[jour].length} créneau{selectedAvailabilities[jour].length > 1 ? 'x' : ''}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {options.creneaux.map(creneau => {
                            const { label, icon: IconComponent } = creneauData[creneau];
                            const isSelected = isAvailabilitySelected(jour, creneau);
                            
                            return (
                              <button
                                key={`${jour}-${creneau}`}
                                type="button"
                                onClick={() => toggleAvailability(jour, creneau)}
                                className={`flex items-center gap-2 p-3 rounded-lg border transition-all text-sm ${
                                  isSelected 
                                    ? 'border-blue-500 bg-blue-500 text-white shadow-md' 
                                    : 'border-gray-300 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                                }`}
                              >
                                <IconComponent size={16} />
                                <div className="text-left">
                                  <div className="font-medium">{creneau.toUpperCase()}</div>
                                  <div className="text-xs opacity-75">{label.split('(')[1]?.replace(')', '')}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {Object.keys(selectedAvailabilities).length > 0 && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-700">
                      <strong>Résumé :</strong> {Object.entries(selectedAvailabilities).map(([jour, creneaux]) => 
                        `${jour.charAt(0).toUpperCase() + jour.slice(1)} (${creneaux.join(', ')})`
                      ).join(' • ')}
                    </div>
                  </div>
                )}
              </div>

              {/* Langue */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <i className="fas fa-language text-blue-600 mr-2"></i>
                  Langue d'enseignement *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {options.langues.map(langue => (
                    <label key={langue} className="flex items-center p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                      <input
                        type="radio"
                        name="langue"
                        value={langue}
                        checked={filters.langue === langue}
                        onChange={(e) => updateFilter('langue', e.target.value as any)}
                        className="mr-3 text-blue-600"
                      />
                      <span>{langueLabels[langue] || langue}</span>
                    </label>
                  ))}
                </div>
              </div>



              {/* Tarif maximum */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-coins text-blue-600 mr-2"></i>
                  Tarif maximum (CHF/h)
                </label>
                <input
                  type="number"
                  min="10"
                  max="100"
                  step="1"
                  value={filters.tarif_max || ''}
                  onChange={(e) => updateFilter('tarif_max', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder={`Ex: ${Math.round(options.tarifStats.avg)}`}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="mt-2 text-sm text-gray-600">
                  <span>Fourchette: {options.tarifStats.min} - {options.tarifStats.max} CHF/h</span>
                  <span className="ml-4">Moyenne: {Math.round(options.tarifStats.avg)} CHF/h</span>
                </div>
              </div>

              {/* Message informatif */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <i className="fas fa-info-circle text-blue-500 mt-1 mr-3"></i>
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">💡 Comment utiliser les filtres :</p>
                    <ul className="space-y-1 text-blue-600">
                      <li>• <strong>Langue</strong> : obligatoire pour la recherche</li>
                      <li>• <strong>Disponibilités</strong> : sélectionnez les jours et créneaux souhaités</li>
                      <li>• <strong>Autres filtres</strong> : optionnels, laissez vide pour voir tous les tuteurs</li>
                      <li>• <strong>Combinaison</strong> : plus vous ajoutez de filtres, plus les résultats seront précis</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex gap-4 pt-6">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Retour
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-lg"
                >
                  <i className="fas fa-search mr-2"></i>
                  Voir les tuteurs
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}; 